import { CircularProgress, Grid, Paper } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { enabledFeatures } from 'config/env'
import { RoleEnum } from 'store/reducers/user'
import { selectUser } from 'store/selectors/user'
import backend from 'utils/backend'

const getInitials = contact => {
  const firstInitial = contact.firstName?.trim()?.[0] || ''
  const lastInitial = contact.lastName?.trim()?.[0] || ''
  return `${firstInitial}${lastInitial}`.toUpperCase() || '?'
}

const UserList = () => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const { username, role } = useSelector(selectUser)

  const { data, isLoading } = useQuery({
    queryKey: ['salesforceAccount', username],
    queryFn: () => backend.get('api/currentuser/account/').then(res => res.data),
    retry: false,
    refetchOnWindowFocus: false
  })

  const contacts = data?.contacts || []
  const totalContacts = contacts.length
  const maxContacts = role === RoleEnum.PREMIUM ? 20 : 2
  const availableSeats = Math.max(maxContacts - totalContacts, 0)

  const you = contacts.find(contact => contact.isYou)
  const otherContacts = contacts
    .filter(contact => !contact.isYou)
    .sort((a, b) => a.name.localeCompare(b.name))
  const sortedContacts = you ? [you, ...otherContacts] : otherContacts

  return (
    <Grid className="user-list">
      <h3>{t.user.profile.usersTitle}</h3>
      <p>{t.user.profile.usersDescription}</p>
      <p className="update-instructions">{t.user.profile.usersInstructions}</p>
      {isLoading && <CircularProgress />}
      {!isLoading && (
        <>
          <Paper className="contacts-details">
            <div className="seats-count">
              <span className="total">{totalContacts}</span>
              <span className="slash">/</span>
              <span className="max">{maxContacts}</span>
            </div>
            <div className="seats-meta">
              <p className="seats-label">{t.user.profile.seatsUsed}</p>
              <p className="seats-available">
                {t.user.profile.seatsAvailable.replace('{availableSeats}', availableSeats)}
              </p>
            </div>
          </Paper>
          <div className="contact-list">
            {sortedContacts.map(contact => (
              <div className="contact-item" key={contact.id || contact.email}>
                <div className="contact-badge">{getInitials(contact)}</div>
                <div className="contact-details">
                  <div className="contact-name-row">
                    <p className="contact-name">{contact.name}</p>
                    {contact.isYou && (
                      <span className="contact-you-badge">{t.user.profile.youBadge}</span>
                    )}
                  </div>
                  <p className="contact-email">{contact.email}</p>
                </div>
                <p className="contact-role">
                  {contact.isAccountManager ? t.user.profile.adminRole : t.user.profile.userRole}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </Grid>
  )
}

UserList.isEnabled = () => enabledFeatures.includes('SUBSCRIBER_INFO_DASHBOARD')
UserList.tabPath = 'users'
UserList.getTabName = t => t.user.profile.usersTitle

export default UserList
